import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { dateTolocale } from 'utils/converter';
import { DateFormat } from 'custom/constants';

const textColor = '#393939';
const primaryBlue = '#092b4a';
const borderColor = primaryBlue;

const cellProperties = {
  padding: 2,
  paddingLeft: 4,
  fontSize: 8,
  color: textColor,
  alignSelf: 'stretch', 
  whiteSpace: 'break-spaces',
  fontFamily: 'NunitoRegular',
  borderRightColor: borderColor,
  borderRightWidth: 1,
};
const headProperties = {
  paddingRight: 5,
  paddingLeft: 5,
  fontSize: 8,
  color: 'white',
  textTransform: 'uppercase',
  whiteSpace: 'break-spaces',
  fontFamily: 'NunitoMedium',
};

const styles = StyleSheet.create({
  headerContainer: { marginTop: 24 },
  tableContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 10,
    marginBottom: 20,
  },
  container: {
    flexDirection: 'row',
    padding: '4px 2px',
    backgroundColor: primaryBlue,
    color: 'white',
    alignItems: 'center',
    flexGrow: 1,
  },
  title: {
    fontSize: 9,
    textTransform: 'uppercase',
    backgroundColor: '#f0f0f0',
    color: primaryBlue,
    padding: '4px 35px',
    textAlign: 'center',
    marginBottom: 5,
    lineHeight: 'normal',
    fontFamily: 'NunitoMedium',
    borderBottom: `2px solid ${primaryBlue}`,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomColor: borderColor,
    borderBottomWidth: 1,
    borderLeftColor: borderColor,
    borderLeftWidth: 1,
  },
  altRow: { backgroundColor: '#f5f5f5', },
  header15: {
    ...headProperties,
    width: '15%',
  },
  cell15: {
    ...cellProperties,
    width: '15%',
  },
  header25: {
    ...headProperties,
    width: '25%',
  },
  cell25: {
    ...cellProperties,
    width: '25%',
  },
  header30: {
    ...headProperties,
    width: '30%',
  },
  cell30: {
    ...cellProperties,
    width: '30%',
  },
  header40: {
    ...headProperties,
    width: '40%',
  },
  cell40: {
    ...cellProperties,
    width: '40%',
  },
  noDataRow: {
    fontSize: 9,
    width: '100%',
    textAlign: 'center',
    padding: 4,
    color: textColor,
    fontFamily: 'NunitoRegular',
  },
  noBorderRight: { borderRight: 'none', },
});

const getPerformedBy = (item) => {
  let length =  item?.flight_checklist?.length - 1;
  let checklist_data = item?.flight_checklist[length]; 
  let user = checklist_data?.user;
  let userDetails = `${user?.first_name} ${user?.last_name} | ${dateTolocale(checklist_data?.created, DateFormat + ' ' + 'HH:mm')?.toUpperCase()}`;
  item.performed_by = user ? userDetails : '';
  return <Text>{user ? userDetails : ''}</Text>;
};

const FLightReport = (props) => {
  const { report, statistics, exclude_disc } = props;

  return (
    <>
      <View style={styles.headerContainer}>
        <Text style={styles.title}>Flight Report</Text>
    
        <View style={styles.tableContainer} wrap={true}>
          <View style={styles.container}>
            <View style={styles.header40}><Text>Line Item</Text></View>
            <View style={styles.header30}><Text>Driver</Text></View>
            <View style={styles.header30}><Text>Quantity</Text></View>
          </View>

          {statistics && statistics.length > 0 ?
            statistics?.map((item, i) =>
              <View style={[
                styles.tableRow,
                i % 2 === 1 && styles.altRow // Apply altRow to odd rows
              ]} key={item.id}>
                <View style={styles.cell40}><Text>{item.description}</Text></View>
                <View style={styles.cell30}><Text>{item.applied_on}</Text></View>
                <View style={[styles.cell30]}><Text>{item.quantity ?( 
                  (() => {
                    let baseValue = item.quantity && !isNaN(parseFloat(item.quantity)) ? 
                      (item.applied_on.includes('KG') ? Math.round(parseFloat(item.quantity) * 100) / 100 : parseFloat(item.quantity)) : 
                      item.applied_on.includes('Auto') || item?.loadingaccessoriesId ? 0 : '';
                    if (exclude_disc === false && !isNaN(parseFloat(item.dis_quantity))) {
                      let finalval = (parseFloat(baseValue) || 0) + parseFloat(item.dis_quantity);
                      if( baseValue === '' && item.dis_quantity === 0) {
                        finalval = '';
                      }
                      finalval = Math.round(finalval * 100) / 100;
                      if (item.applied_on && item.applied_on.includes('Manual') && !item?.user && !item?.loadingaccessoriesId) {
                        finalval = '';
                      }
                      return finalval;
                    }
                    baseValue = Math.round(baseValue * 100) / 100;
                    if (item.applied_on && item.applied_on.includes('Manual') && !item?.user && !item?.loadingaccessoriesId) {
                      baseValue = '';
                    }
                    return baseValue;
                  })()
                ): ''}</Text></View>
                {/* <Text style={[styles.cell30, styles.noBorderRight]}>{item.quantity ?( 
                  item.applied_on.includes('KG') ? Math.round(item.quantity*100)/100 : item.quantity ): ''}</Text>            */}
              </View>
            ): <View  style={styles.noDataRow}>
              <Text>No Data</Text>
            </View>}
        </View>

        <View style={styles.tableContainer} wrap={false}>
          <View style={styles.container}>
            <View style={styles.header30}><Text>Checks</Text></View>
            <View style={styles.header15}><Text>Roles</Text></View>
            <View style={styles.header25}><Text>Entered By</Text></View>
            <View style={styles.header30}><Text>Remarks</Text></View>
          </View>

          {report && report.length > 0 ?
            report?.map((item, i) =>
              <View style={[
                styles.tableRow,
                i % 2 === 1 && styles.altRow // Apply altRow to odd rows
              ]} key={item.id}>
                <View style={styles.cell30}><Text>{item.description}</Text></View>
                <View style={styles.cell15}><Text>{item.role}</Text></View>
                <View style={styles.cell25}><Text>{getPerformedBy(item)}</Text></View>
                <View style={styles.cell30}><Text>{item.flight_checklist[item?.flight_checklist?.length - 1]?.remark}</Text></View>
              </View>
            ): <View style={styles.noDataRow}>
              <Text>No Data</Text>
            </View>}
        </View>

      </View>

    </>
  );
};
  
export default FLightReport;